/*
===============================================================================================================
                                 Copyright 2026-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, { useContext, useMemo } from 'react';

import InlineEditableText from '@/components/inline-editable-text';
import SimpleButton from '@/components/button-simple';
import { LocaleContext } from '@/contexts/locale-context';
import { unEscapeCustomText } from '@/modules/page-customisation/utilities';
import { getStaticCdnPath } from '@/utilities/static-build';
import { formatLocaleCurrency } from '@/utilities/locale-formatting';

const REFUND_METHODS_TEXT_ENUM = {
  title: 'title',
  subtitle: 'subtitle',
  STANDARD_REFUND_title: 'STANDARD_REFUND_title',
  STANDARD_REFUND_description: 'STANDARD_REFUND_description',
  STORE_CREDIT_title: 'STORE_CREDIT_title',
  STORE_CREDIT_description: 'STORE_CREDIT_description',
};

const defaultTextKeys = {
  [REFUND_METHODS_TEXT_ENUM.title]: 'customisation.refundMethodsQuestionTitle',
  [REFUND_METHODS_TEXT_ENUM.subtitle]: 'customisation.refundMethodsQuestionSubtitle',
  [REFUND_METHODS_TEXT_ENUM.STANDARD_REFUND_title]: 'customisation.refundMethodsFirstOptionTitle',
  [REFUND_METHODS_TEXT_ENUM.STANDARD_REFUND_description]: 'customisation.refundMethodsFirstOptionSubtitle',
  [REFUND_METHODS_TEXT_ENUM.STORE_CREDIT_title]: 'customisation.refundMethodsSecondOptionTitle',
  [REFUND_METHODS_TEXT_ENUM.STORE_CREDIT_description]: 'customisation.refundMethodsSecondOptionSubtitle',
};

/**
 * A block for displaying and editing refund method options
 * @param {object} props
 * @param {object} props.customTextValues - The text values for the refund methods block
 * @param {boolean} props.editEnabled - Whether or not the edit mode for the refund methods block is enabled
 * @param {object} props.product - The product data to display in the block header
 * @param {Function} props.onChange - A function called when a card field is updated
 * @return {JSX.Element}
 */
export default function EditableRefundMethodsBlock({
  customTextValues,
  editEnabled,
  product,
  onChange,
}) {
  const { translate, getIntlLocale } = useContext(LocaleContext);
  const intlLocale = getIntlLocale();

  const formatPrice = (price) => (
    intlLocale && product?.priceCurrency
      ? formatLocaleCurrency(Number(price || 0), product.priceCurrency, intlLocale)
      : `$${Number(price || 0).toFixed(2)}`
  );

  const resolvedValues = useMemo(() => ({
    [REFUND_METHODS_TEXT_ENUM.title]: customTextValues?.[REFUND_METHODS_TEXT_ENUM.title],
    [REFUND_METHODS_TEXT_ENUM.subtitle]: customTextValues?.[REFUND_METHODS_TEXT_ENUM.subtitle],
    [REFUND_METHODS_TEXT_ENUM.STANDARD_REFUND_description]: customTextValues?.[REFUND_METHODS_TEXT_ENUM.STANDARD_REFUND_description],
    [REFUND_METHODS_TEXT_ENUM.STORE_CREDIT_description]: customTextValues?.[REFUND_METHODS_TEXT_ENUM.STORE_CREDIT_description],
  }), [customTextValues]);

  const onFieldChange = (fieldId, value) => {
    onChange({
      ...customTextValues,
      [fieldId]: unEscapeCustomText(value),
    });
  };

  const renderEditableText = ({
    fieldId,
    maxLength = 120,
    textAreaMode = false,
    fontWeight = 'var(--font-weight-body)',
    fontSize = 'var(--font-size-body)',
    externalWrapperWidth = null,
    withTextOverflow = false,
    minHeight = null,
  }) => (
    <InlineEditableText
      inputSelectorId={`refund-methods-${fieldId}`}
      validationRules={{
        onSubmit: ['nonEmpty'],
        onChange: ['nonEmpty'],
      }}
      maxLength={maxLength}
      defaultValue={resolvedValues[fieldId] || ''}
      defaultResetValue={translate(defaultTextKeys[fieldId])}
      textAreaMode={textAreaMode}
      smallFontStyling
      shouldTranslateResetValue={false}
      readOnly={!editEnabled}
      fontWeight={fontWeight}
      fontSize={fontSize}
      externalWrapperWidth={externalWrapperWidth}
      withTextOverflow={withTextOverflow}
      minHeight={minHeight}
      onChange={(val) => onFieldChange(fieldId, val)}
    />
  );

  return (
    <div className="refund-methods-wrapper">
      <div className="product-header">
        <div className="header-main">
          <img className="product-image" src={`${getStaticCdnPath()}${product?.imageUrl || ''}`} alt={product?.productName || ''} />
          <div className="product-meta">
            <div className="product-title">{product?.productName}</div>
            <div className="product-attrs">
              <div>{formatPrice(product?.price)}</div>
              <div>{`${translate('constants.size')} ${product?.size || ''}`}</div>
              <div>{product?.color || ''}</div>
            </div>
          </div>
        </div>
        <div className="close-corner" aria-hidden>
          <span className="close-icon">×</span>
        </div>
      </div>

      <div className="section-title">
        {renderEditableText({
          fieldId: REFUND_METHODS_TEXT_ENUM.title,
          maxLength: 80,
          fontWeight: 'var(--font-weight-H1)',
          fontSize: '16px',
          withTextOverflow: true,
          externalWrapperWidth: 560,
        })}
      </div>
      <div className="section-subtitle">
        {renderEditableText({
          fieldId: REFUND_METHODS_TEXT_ENUM.subtitle,
          maxLength: 120,
          textAreaMode: true,
          withTextOverflow: true,
          externalWrapperWidth: 560,
        })}
      </div>

      <div className="options-wrapper">
        <div className="refund-option">
          <div className="option-radio" />
          <div className="option-texts">
            <div className="option-title">
              {translate(defaultTextKeys[REFUND_METHODS_TEXT_ENUM.STANDARD_REFUND_title])}
            </div>
            {renderEditableText({
              fieldId: REFUND_METHODS_TEXT_ENUM.STANDARD_REFUND_description,
              fontSize: '14px',
              maxLength: 120,
              textAreaMode: true,
              externalWrapperWidth: 480,
            })}
          </div>
          <div className="option-price">{formatPrice(product?.price)}</div>
        </div>

        <div className="refund-option">
          <div className="option-radio" />
          <div className="option-texts">
            <div className="option-title">
              {translate(defaultTextKeys[REFUND_METHODS_TEXT_ENUM.STORE_CREDIT_title])}
            </div>
            {renderEditableText({
              fieldId: REFUND_METHODS_TEXT_ENUM.STORE_CREDIT_description,
              fontSize: '14px',
              maxLength: 120,
              textAreaMode: true,
              externalWrapperWidth: 480,
            })}
          </div>
          <div className="option-price">{formatPrice(product?.price)}</div>
        </div>
      </div>

      <div className="buttons-wrapper">
        <SimpleButton
          inputSelectorId="refund-methods-confirm"
          inputHeight={42}
          inputWidth={300}
          centered
          disabled
          readOnly
          hasMargin={false}
          backgroundColor="--color-primary-dark"
          textColor="--color-white"
        >
          {translate('constants.confirm')}
        </SimpleButton>
        <SimpleButton
          inputSelectorId="refund-methods-back"
          inputHeight={35}
          inputWidth={150}
          centered
          disabled
          readOnly
          hasMargin={false}
          backgroundColor="--color-white"
          textColor="--color-primary-dark"
          borderColor="--color-primary-dark"
        >
          {translate('constants.back')}
        </SimpleButton>
      </div>

      <style jsx>
        {`
          .refund-methods-wrapper {
            margin: 0 auto;
            max-width: 620px;
            width: 100%;
          }

          .section-title,
          .section-subtitle {
            line-height: 1.4;
          }

          .product-header {
            position: relative;
            width: 100%;
            border-bottom: 1px solid var(--color-light-grey);
            margin-bottom: 24px;
            padding-bottom: 12px;
          }

          .header-main {
            display: flex;
            align-items: flex-start;
            column-gap: 14px;
            width: 100%;
          }

          .product-meta {
            display: flex;
            flex-direction: column;
            row-gap: 8px;
            min-width: 0;
          }

          .product-image {
            width: 106px;
            height: 106px;
            object-fit: cover;
            border-radius: var(--border-radius-small);
            flex-shrink: 0;
          }

          .product-title {
            margin-top: 2px;
            font-size: var(--font-size-H2);
            font-weight: var(--font-weight-H1);
            color: var(--color-primary-dark);
            line-height: 1.35;
          }

          .product-attrs {
            text-align: left;
            color: var(--color-primary-dark);
            font-size: var(--font-size-H3);
            line-height: 1.35;
          }

          .close-corner {
            position: absolute;
            right: 0;
            top: 0;
            width: 44px;
            height: 44px;
            clip-path: polygon(100% 0, 0 0, 100% 100%);
            background: var(--color-primary-dark);
            display: flex;
            align-items: flex-start;
            justify-content: flex-end;
          }

          .close-icon {
            margin-top: -5px;
            margin-right: 2px;
            color: var(--color-white);
            font-size: 32px;
            line-height: 1;
          }

          .section-subtitle {
            margin-top: 2px;
            margin-bottom: 12px;
          }

          .options-wrapper {
            display: flex;
            flex-direction: column;
            row-gap: 6px;
          }

          .refund-option {
            display: flex;
            align-items: center;
            column-gap: 18px;
            border: 1px solid var(--color-black);
            background-color: var(--color-white);
            border-radius: var(--border-radius);
            padding: 14px 18px;
          }

          .option-radio {
            width: 17px;
            height: 17px;
            border-radius: 50%;
            border: 1px solid var(--color-primary-dark);
            background: radial-gradient(circle, var(--color-primary-dark) 0 3px, transparent 4px);
            flex-shrink: 0;
            align-self: center;
          }

          .option-texts {
            flex: 1;
          }

          .option-title {
            font-family: var(--font-family-body-sans), Arial, Helvetica, sans-serif;
            font-size: var(--font-size-body);
            font-weight: var(--font-weight-H1);
          }

          .option-price {
            font-size: var(--font-size-body);
            font-weight: var(--font-weight-H1);
            color: var(--color-primary-dark);
            white-space: nowrap;
            width: 74px;
            text-align: right;
            flex-shrink: 0;
            align-self: center;
          }

          .buttons-wrapper {
            margin-top: 28px;
            display: flex;
            flex-direction: column;
            row-gap: 18px;
            align-items: center;
          }
        `}
      </style>
    </div>
  );
}
